import { isNonEmpty } from '@sgme/fp';
import { AlertBanner } from 'components/shared/AlertBanner/AlertBanner';
import { ConfirmationBanner } from 'components/shared/ConfirmationBanner/ConfirmationBanner';
import { FormattedMessage } from 'components/shared/Intl';
import { IntlKey } from 'locales';
import { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getBlotterPanelItemId } from 'state/blotters/blotterPanel/blotterPanel.selector';
import { emailServiceModalClosed } from 'state/emailService/emailService.actions';
import {
  getErrors,
  getPreviewErrors,
  getWarnings,
  isEmailServiceFormValid,
  isEmailServiceModalOpen,
  isEmailServiceSendingEmail,
  isEmailServiceSentEmailWithoutWarnings,
  isEmailServiceSentEmailWithWarnings,
  isEmailServiceSubmissionFailed,
} from 'state/emailService/emailService.selectors';
import { emailServiceFormSubmissionRequestedThunk } from 'state/emailService/emailService.thunks';
import type { ThunkDispatch } from 'state/state.common';
import styled from 'styled-components';
import { isStackTrace } from 'utils/errors/errors';

import { EmailServiceForm } from '../EmailServiceForm/EmailServiceForm';
import { EmailServiceModalActions } from '../EmailServiceModalActions/EmailServiceModalActions';

const StyledModal = styled(Modal)`
  .modal-body {
    > .form-group:last-child {
      margin-bottom: 0;
    }
  }
`;
const StyledConfirmationBanner = styled(ConfirmationBanner)`
  bottom: 0;
  left: 0;
  right: 0;
`;
const StyledAlertBanner = styled(AlertBanner)`
  bottom: 0;
  left: 0;
  right: 0;
`;
const StyledUnorderedList = styled.ul`
  list-style-type: '- ';
`;

// TODO: Unit test
export function EmailServiceModal() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const dispatch = useDispatch<ThunkDispatch>();

  const isOpen = useSelector(isEmailServiceModalOpen);

  const isFormValid = useSelector(isEmailServiceFormValid);
  const referenceId = useSelector(getBlotterPanelItemId);

  const bannerE2e = 'email-service-alert-banner';
  const bannerClass = 'position-absolute pt-1';

  const isSending = useSelector(isEmailServiceSendingEmail);
  const isSentWithoutWarnings = useSelector(isEmailServiceSentEmailWithoutWarnings);
  const showNoErrorBanner = isConfirmed && (isSending || isSentWithoutWarnings);
  const color = isSending ? 'filling-info' : 'success';
  const icon = isSending ? 'autorenew' : 'check_circle';
  const spin = isSending;
  const message = isSending ? 'emailService.status.message.sending' : 'emailService.status.message.sentWithoutWarnings';

  const previewErrors = useSelector(getPreviewErrors);

  const isSentWithWarnings = useSelector(isEmailServiceSentEmailWithWarnings);
  const warnings = useSelector(getWarnings);
  const isSubmissionFailed = useSelector(isEmailServiceSubmissionFailed);
  const errors = useSelector(getErrors);
  const showWarningsOrErrorsBanner = isConfirmed && (isSentWithWarnings || isSubmissionFailed);

  const [alertHeader, ...alertList] = isSubmissionFailed ? errors : warnings;
  const alertIcon = 'error_outline';
  const alertColor = isSentWithWarnings ? 'warning' : 'danger';

  const onCancel = useCallback(() => {
    dispatch(emailServiceModalClosed());
  }, [dispatch]);

  const onSubmit = useCallback(() => {
    setIsSubmitted(true);
    setIsConfirmed(false);
  }, []);

  const onConfirm = useCallback(() => {
    dispatch(emailServiceFormSubmissionRequestedThunk());
    setIsSubmitted(false);
    setIsConfirmed(true);
  }, [dispatch]);

  const onConfirmationCancelled = useCallback(() => {
    setIsSubmitted(false);
    setIsConfirmed(false);
  }, []);

  const onBannerDismissed = useCallback(() => {
    setIsSubmitted(false);
    setIsConfirmed(false);
  }, []);

  return (
    <StyledModal show={isOpen} backdrop centered contentClassName="position-relative">
      <div className="modal-header d-flex align-item-start" data-e2e="email-service-modal-header">
        <div className="flex-fill">
          <span className="text-secondary" data-e2e="email-service-reference-id">
            {referenceId}
          </span>
          <h4 className="modal-title">
            <FormattedMessage id="emailService.title" />
          </h4>
        </div>
        <button
          type="button"
          className="btn btn-lg btn-flat-secondary"
          aria-label="Close"
          data-e2e="email-service-modal-close"
          onClick={onCancel}
        >
          <i className="icon">clear</i>
        </button>
      </div>

      <EmailServiceForm />

      <EmailServiceModalActions canSubmit={isFormValid && !isSending} onCancel={onCancel} onSubmit={onSubmit} />

      {isSubmitted && (
        <StyledConfirmationBanner
          className="position-absolute"
          cancelButtonText="form.No"
          confirmButtonText="form.Yes"
          e2e="email-service-confirmation-banner"
          message="emailService.confirmation.message"
          onCancel={onConfirmationCancelled}
          onConfirm={onConfirm}
        />
      )}

      {showNoErrorBanner && (
        <StyledAlertBanner
          dismissible={!isSending}
          onDismissed={onBannerDismissed}
          spin={spin}
          className={bannerClass}
          e2e={bannerE2e}
          color={color}
          icon={icon}
        >
          <FormattedMessage id={message} />
        </StyledAlertBanner>
      )}

      {isNonEmpty(previewErrors) && (
        <StyledAlertBanner className={bannerClass} e2e={bannerE2e} color={alertColor}>
          <>
            <FormattedMessage id={'unknownError'} />
            <StyledUnorderedList className="ps-4 list-group">
              {previewErrors.map(({ originMessage }, i) =>
                originMessage && !isStackTrace(originMessage) ? <li key={i}>{originMessage}</li> : null,
              )}
            </StyledUnorderedList>
          </>
        </StyledAlertBanner>
      )}

      {showWarningsOrErrorsBanner && (
        <StyledAlertBanner
          dismissible
          onDismissed={onBannerDismissed}
          className={bannerClass}
          e2e={bannerE2e}
          color={alertColor}
          icon={alertIcon}
        >
          <>
            <div className="ps-2">
              <FormattedMessage id={alertHeader as IntlKey} defaultMessage={alertHeader} />
            </div>
            {alertList.length > 0 && (
              <StyledUnorderedList className="ps-4 list-group">
                {alertList.map(message => (
                  <FormattedMessage key={message} id={message as IntlKey} defaultMessage={message} tagName="li" />
                ))}
              </StyledUnorderedList>
            )}
          </>
        </StyledAlertBanner>
      )}
    </StyledModal>
  );
}

EmailServiceModal.displayName = 'EmailServiceModal';
